import * as React from "react";
import * as S from "@styled";

import { IElement, IParameterSchema } from "../model/IProcessData";
import { ParameterEditorValue, ParameterEditorValueWithType } from "../model/ParameterEditorValue";

import { default as InputEditor } from "./InputEditor";
import { default as LogResultEditor } from "./LogResultEditor";
import { default as DisposeEditor } from "./DisposeEditor";
import { default as ParameterInput } from "./ParameterInput";
import { default as PromoteResultEditor } from "./PromoteResultEditor";
import { SwitchCollapse } from "./SwitchCollapse";
import CustomToggle from "shared/components/CustomToggle";

export const ExpressionParameterName = "variableExpression";

export const ExpressionEditor: React.FC<ExpressionProps> = (props) => {
    const variableNameVarName = "variableName";
    const returnType = "returnType";
    const shouldAssignVariableVarName = "shouldAssignVariable";
    const elemParameters = props.element.parameters as ExpressionParameters;
    const parameterSchema: IParameterSchema = {
        inputType: "expression",
        type: "dynamic",
        name: ExpressionParameterName,
        parameterType: "TextParameter",
        defaultValue: "",
        isSecret: false
    };

    const isAdvancedOptionOpenByDefault =
        (elemParameters.returnType || props.element.promoteResultAs) != null
        || props.element.shouldNotLogResult
        || props.element.shouldDispose
        || props.element.shouldRetry;

    function handleToggleAssignVariableChange(checked: boolean, event: any, id: string) {
        props.onChangeAssignVariable(
            {
                mode: "toggle",
                value: checked
            },
            shouldAssignVariableVarName,
            (checked ? "variable" : ""),
            variableNameVarName);
    }

    function handleVariableNameChange(event: React.ChangeEvent<HTMLInputElement>) {
        const newVariableName = event.target.value;
        props.onChangeAssignVariable(
            elemParameters.shouldAssignVariable,
            shouldAssignVariableVarName,
            newVariableName,
            variableNameVarName);
    }

    const inStatementMode = elemParameters.useStatementMode && elemParameters.useStatementMode.value || false;
    const advancedSettingEditors = <>
        <LogResultEditor key="logResultEditor" elementId={props.id} />
        <DisposeEditor key="disposeEditor" elementId={props.id} />
        <PromoteResultEditor key="promoteResultEditor" id={props.id} />
        <InputEditor
            name={returnType}
            key="returnTypeEditor"
            tooltip={FRENDS.Localizations.ReturnTypeToolTip}
            label={FRENDS.Localizations.Type}
            elementId={props.id}
            value={elemParameters.returnType || ""}
        />
    </>;

    const assignVariable = elemParameters.shouldAssignVariable && elemParameters.shouldAssignVariable.value;

    return (
        <div>
            <SwitchCollapse
                isOpenByDefault={isAdvancedOptionOpenByDefault}
                childrenCollapsed={null}
                childrenOpen={advancedSettingEditors}
            />
            <hr />
            <S.FormGroup key="assignVarToggle" $inline={true}>
                <S.Label>
                    {FRENDS.Localizations.AssignVariable}
                </S.Label>
                <CustomToggle
                    checked={assignVariable}
                    onChange={handleToggleAssignVariableChange}
                    disabled={props.disabled}
                />
            </S.FormGroup>
            {assignVariable ?
                <S.FormGroup>
                    <S.Input
                        isFullWidth={true}
                        type="text"
                        value={elemParameters.variableName || ""}
                        onChange={handleVariableNameChange}
                        disabled={props.disabled}
                    />
                </S.FormGroup>
                : null}
            <ParameterInput
                name={ExpressionParameterName}
                label={inStatementMode ? FRENDS.Localizations.Statement : FRENDS.Localizations.Expression}
                elementId={props.id}
                value={elemParameters[ExpressionParameterName]}
                // Passing a empty parameterSchema is enough for the ParameterInput to generate the type selection menu
                parameterSchema={parameterSchema}
            />
        </div>
    );
};

interface ExpressionProps {
    id: string;
    element: IElement;
    disabled: boolean | undefined;
    onChangeAssignVariable: (toggleValue: ParameterEditorValueWithType<boolean>, toggleKey: string, variableValue: string, variableKey: string) => void;
}

export interface ExpressionParameters {
    variableName: string;
    returnType: string;
    shouldAssignVariable: ParameterEditorValueWithType<boolean>;
    useStatementMode: ParameterEditorValueWithType<boolean>;
    variableExpression: ParameterEditorValue;
}
